import Script from 'next/script';
import { useEffect } from 'react';

import {
  buildFeedbackEmbedUrl,
  WHATS_NEW_FEEDBACK_FORM_ID
} from '@lib/whats-new/feedback';

declare global {
  interface Window {
    Tally?: { loadEmbeds: () => void };
  }
}

const TALLY_EMBED_SCRIPT_URL = 'https://tally.so/widgets/embed.js';
// Holds the space until Tally's script resizes the frame to fit the form.
const INITIAL_FRAME_HEIGHT = 520;

const loadTallyEmbeds = () => window.Tally?.loadEmbeds();

// Tally's own snippet does this when its script is blocked: the form still
// loads, just without resizing itself.
const loadTallyEmbedsWithoutScript = () =>
  document
    .querySelectorAll<HTMLIFrameElement>('iframe[data-tally-src]:not([src])')
    .forEach((frame) => {
      frame.src = frame.dataset.tallySrc ?? '';
    });

export const FeedbackForm = ({
  hiddenFields
}: {
  hiddenFields: Record<string, string>;
}) => {
  const embedUrl = buildFeedbackEmbedUrl(
    hiddenFields,
    WHATS_NEW_FEEDBACK_FORM_ID
  );

  // After a client-side navigation the script has already loaded, so its
  // onLoad will not fire again for this frame.
  useEffect(() => {
    loadTallyEmbeds();
  }, []);

  if (!embedUrl) return null;

  return (
    <>
      <iframe
        data-tally-src={embedUrl}
        loading="lazy"
        width="100%"
        height={INITIAL_FRAME_HEIGHT}
        title="Send us feedback"
        className="block w-full border-0"
      />
      <Script
        src={TALLY_EMBED_SCRIPT_URL}
        strategy="lazyOnload"
        onLoad={loadTallyEmbeds}
        onError={loadTallyEmbedsWithoutScript}
      />
    </>
  );
};
